import React, { Component } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Input,
  FormGroup,
  Label,
  Spinner,
  Table,
  Button,
} from "reactstrap";
import { withRouter, Link } from "react-router-dom";
import { withTranslation } from "react-i18next";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { getFoodAnalytics, exportFoodAnalytics } from "../../Endpoint/Admin";
import { downloadBase64File } from "../../utils/exportToExcel";
import { toast } from "react-toastify";

class FoodAnalytics extends Component {
  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      startDate: this.getDefaultStartDate(),
      endDate: this.getDefaultEndDate(),
      analyticsData: null,
      breadcrumbItems: [
        { title: "Analytics", link: "/analytics" },
        { title: "Food Analytics", link: "#" },
      ],
    };
  }

  getDefaultStartDate = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split("T")[0];
  };

  getDefaultEndDate = () => {
    return new Date().toISOString().split("T")[0];
  };

  componentDidMount() {
    this.loadAnalytics();
  }

  loadAnalytics = async () => {
    this.setState({ loading: true });
    const { startDate, endDate } = this.state;

    const response = await getFoodAnalytics({ startDate, endDate });
    this.setState({
      analyticsData: response?.success ? response.data : null,
      loading: false,
    });
  };

  handleDateChange = (e) => {
    this.setState({ [e.target.name]: e.target.value });
  };

  handleApplyFilters = () => {
    this.loadAnalytics();
  };

  handleExport = async () => {
    this.setState({ exporting: true });
    try {
      const { startDate, endDate } = this.state;
      const res = await exportFoodAnalytics({ startDate, endDate });
      if (res?.success && res.data) {
        downloadBase64File(res.data.base64, res.data.fileName);
      } else {
        toast.error("Export failed");
      }
    } catch (err) {
      toast.error("Export failed");
    }
    this.setState({ exporting: false });
  };

  formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    }).format(amount || 0);
  };

  formatNumber = (num) => {
    return new Intl.NumberFormat("en-US").format(num || 0);
  };

  render() {
    const { loading, startDate, endDate, analyticsData } = this.state;

    const summary = analyticsData?.summary || {};
    const popularItems = analyticsData?.popularItems || [];
    const ordersByHour = analyticsData?.ordersByHour || [];
    const ordersByType = analyticsData?.ordersByType || [];

    return (
      <React.Fragment>
        <div className="page-content">
          <Container fluid>
            <Breadcrumbs title="Food Analytics" breadcrumbItems={this.state.breadcrumbItems} />

            {/* Filters */}
            <Card>
              <CardBody>
                <Row className="align-items-end">
                  <Col md={3}>
                    <FormGroup className="mb-0">
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        name="startDate"
                        value={startDate}
                        onChange={this.handleDateChange}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup className="mb-0">
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        name="endDate"
                        value={endDate}
                        onChange={this.handleDateChange}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={2}>
                    <Button color="primary" onClick={this.handleApplyFilters}>
                      Apply
                    </Button>
                    <Button color="success" className="ml-2" disabled={this.state.exporting} onClick={this.handleExport}>
                      {this.state.exporting ? <Spinner size="sm" className="mr-1" /> : <i className="ri-file-excel-2-line mr-1"></i>} Export XLS
                    </Button>
                  </Col>
                  <Col md={4} className="text-right">
                    <Link to="/analytics" className="btn btn-outline-secondary">
                      Back to Dashboard
                    </Link>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
                <p className="mt-2">Loading food analytics...</p>
              </div>
            ) : (
              <>
                {/* Summary Cards */}
                <Row>
                  <Col md={4}>
                    <Card className="bg-success text-white">
                      <CardBody>
                        <h6 className="text-white-50">Total Orders</h6>
                        <h3>{this.formatNumber(summary.totalOrders)}</h3>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="bg-primary text-white">
                      <CardBody>
                        <h6 className="text-white-50">Total Revenue</h6>
                        <h3>{this.formatCurrency(summary.totalRevenue)}</h3>
                      </CardBody>
                    </Card>
                  </Col>
                  <Col md={4}>
                    <Card className="bg-info text-white">
                      <CardBody>
                        <h6 className="text-white-50">Avg Order Value</h6>
                        <h3>{this.formatCurrency(summary.avgOrderValue)}</h3>
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                <Row>
                  {/* Popular Items */}
                  <Col md={6}>
                    <Card>
                      <CardBody>
                        <h4 className="card-title mb-4">Popular Items</h4>
                        {popularItems.length === 0 ? (
                          <p className="text-muted text-center">No data available</p>
                        ) : (
                          <Table className="table-sm mb-0">
                            <thead>
                              <tr>
                                <th>#</th>
                                <th>Item</th>
                                <th className="text-right">Quantity</th>
                                <th className="text-right">Revenue</th>
                              </tr>
                            </thead>
                            <tbody>
                              {popularItems.slice(0, 10).map((item, index) => (
                                <tr key={item._id}>
                                  <td>{index + 1}</td>
                                  <td>{item.name}</td>
                                  <td className="text-right">{this.formatNumber(item.quantity)}</td>
                                  <td className="text-right">{this.formatCurrency(item.revenue)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </CardBody>
                    </Card>
                  </Col>

                  {/* Orders by Type */}
                  <Col md={6}>
                    <Card>
                      <CardBody>
                        <h4 className="card-title mb-4">Orders by Type</h4>
                        {ordersByType.length === 0 ? (
                          <p className="text-muted text-center">No data available</p>
                        ) : (
                          <Table className="table-sm mb-0">
                            <thead>
                              <tr>
                                <th>Type</th>
                                <th className="text-right">Orders</th>
                                <th className="text-right">Revenue</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ordersByType.map((item, index) => (
                                <tr key={index}>
                                  <td>{item.type}</td>
                                  <td className="text-right">{this.formatNumber(item.orders)}</td>
                                  <td className="text-right">{this.formatCurrency(item.revenue)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        )}
                      </CardBody>
                    </Card>
                  </Col>
                </Row>

                {/* Peak Hours */}
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Orders by Hour</h4>
                    {ordersByHour.length === 0 ? (
                      <p className="text-muted text-center">No data available</p>
                    ) : (
                      <div className="d-flex flex-wrap">
                        {Array.from({ length: 24 }, (_, i) => {
                          const hourData = ordersByHour.find((h) => h.hour === i);
                          const orders = hourData?.orders || 0;
                          const maxOrders = Math.max(...ordersByHour.map((h) => h.orders), 1);
                          const height = Math.max((orders / maxOrders) * 60, 5);
                          return (
                            <div
                              key={i}
                              className="text-center mx-1"
                              style={{ width: "30px" }}
                            >
                              <div
                                className="bg-primary mx-auto"
                                style={{
                                  width: "20px",
                                  height: `${height}px`,
                                  borderRadius: "3px",
                                }}
                                title={`${orders} orders`}
                              ></div>
                              <small className="text-muted">{i}</small>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardBody>
                </Card>
              </>
            )}
          </Container>
        </div>
      </React.Fragment>
    );
  }
}

export default withRouter(withTranslation()(FoodAnalytics));
